import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import { CSVLink } from 'react-csv'
import styled from 'styled-components'
import {
  Badge,
  Table,
  Modal,
  Alert,
  Space,
  Typography,
  Form,
  Switch,
  Select,
  Row,
  Col,
  Tooltip
} from 'antd'
import { QuestionCircleFilled } from '@ant-design/icons'
import { colours, sizes } from '@qflow/theme'

import './Settings.css'

import {
  getIdFromRole,
  ROLE_INFORMATION,
  USER_ROLES
} from 'portal/lib/constants/UserRole'
import { matchesEmailFilter } from 'portal/lib/functions/userSettingsHelpers'
import { useUserAccountRole } from 'portal/lib/hooks/useUserAccountRole'
import {
  ColourButton,
  ClickableLink,
  Flex,
  icons,
  Label,
  NavButtonDecorator,
  SmallSpinner,
  Spacer,
  UserCard,
  UserAvatar
} from 'portal/lib/primitives'
import { FORBIDDEN } from 'portal/routes'

import { accountProjectsGet } from 'portal/store/allAccountProjects/actions'
import { accountConnectionsSummaryLoad } from 'portal/store/accountConnectionsSummary/actions'
import { accountLoad } from 'portal/store/accountDetails/actions'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

import {
  userEditStart,
  userUpdateStart
} from 'portal/store/userDetails/actions'
import { projectUsersLoad } from 'portal/store/projectUsers/actions'
import { usersLoad } from 'portal/store/users/actions'

import { getKeyFromIds } from 'portal/store/users/reducer'
import { ProjectSelector } from './ProjectSelector'

import { getBrowserShortDateFormat } from 'portal/lib/functions/dateFormatHelpers'

const { Title } = Typography

const getTableData = usersState => {
  const data = []
  if (usersState.loading == null || typeof usersState.users == 'undefined') {
    return data
  }

  const minDate = moment.utc('0001-01-01')

  Object.values(usersState.users).forEach(user => {
    data.push({
      userId: user.userId,
      displayName: user.displayName,
      email: user.email,
      lastActivity:
        user.lastActivity && moment.utc(user.lastActivity).isAfter(minDate)
          ? moment(user.lastActivity)
              .local()
              .format(getBrowserShortDateFormat())
          : 'No activity',
      userData: user
    })
  })

  data.sort((a, b) => {
    if (a.displayName && b.displayName) {
      return a.displayName
        .toUpperCase()
        .localeCompare(b.displayName.toUpperCase())
    }
    return 0
  })

  return data
}

export function AccountSettingsTable() {
  const [filteredInfo, setFilteredInfo] = useState({})
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { accountId } = useParams()

  const stateAccount = useSelector(state => state.accountDetails)
  const stateUsers = useSelector(state => state.users)
  const stateAccountProjects = useSelector(state => state.accountProjects)
  const auth = useSelector(state => state.auth)
  const accountUser = useSelector(state => state.userDetails)

  const { isAdmin, roleLoaded } = useUserAccountRole(accountId)

  useEffect(() => {
    dispatch(accountProjectsGet(accountId))
    dispatch(accountLoad(accountId))
    dispatch(accountConnectionsSummaryLoad(accountId))
  }, [accountId, dispatch])

  if (roleLoaded && !isAdmin) {
    navigate(FORBIDDEN)
  }

  const accountSummary = useSelector(state => state.accountConnectionsSummary)

  const [form] = Form.useForm()

  const [view, setView] = useState('users')
  const [selectedProjectId, setSelectedProjectId] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [alert, setAlert] = useState(null)

  // If querystring has view, set that as the view
  const [searchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.has('view')) {
      setView(searchParams.get('view'))
    }
  }, [searchParams])

  useEffect(() => {
    if (!accountUser.loading && accountUser.error != null) {
      setAlert(
        <>
          {' '}
          <br />
          <Alert
            key="error"
            message={'Something Went Wrong'}
            description={'Please try submitting again.'}
            type="error"
            closable
          />
        </>
      )
    }
    if (!accountUser.loading && accountUser.error == null) {
      setAlert(null)
      setModalOpen(false)
      dispatch(usersLoad(accountId))
    }
  }, [dispatch, accountId, accountUser.loading, accountUser.error])

  // Load Project Users in to Redux state
  useEffect(() => {
    dispatch(projectUsersLoad(accountId, selectedProjectId, false))
  }, [accountId, dispatch, selectedProjectId])

  const changeView = newView => {
    setView(newView)

    const VIEW_CHANGED = 'frontend_account_settings_view_changed'
    dispatch(
      trackMixpanel(VIEW_CHANGED, {
        view: newView,
        account_id: accountId
      })
    )
  }

  const getFormData = user => {
    const formData = []
    stateAccountProjects?.projects?.forEach(project => {
      const userPermission = user.projectPermissions.filter(
        p => p.projectId === project.projectId
      )
      const userSetting = user.projectSettings.filter(
        p => p.projectId === project.projectId
      )

      let accessEnabled = false
      let role = 'Reader'
      if (userPermission && userPermission.length > 0) {
        accessEnabled = true
        role = USER_ROLES[userPermission[0].userRoleId]
      }

      formData.push({
        project: project.name,
        projectId: project.projectId,
        notificationsEnabled:
          user.notificationsEnabled && userSetting[0]?.notificationsEnabled,
        accessEnabled: accessEnabled,
        role: role
      })
    })
    return { projectPermissions: formData }
  }

  const accountUsersFiltered = getTableData(stateUsers)
    ?.filter(user =>
      matchesEmailFilter(auth.isQualisFlowInternal, '', user.email)
    )
    .sort((a, b) => {
      if (a.displayName && b.displayName) {
        return a.displayName
          .toUpperCase()
          .localeCompare(b.displayName.toUpperCase())
      }
      return 0
    })

  // Create filter options for the table's Users column based on the users in the account and
  // deduplicate them in case of users with same display name
  const uniqueDisplayNames = new Set()
  const accountUsersFilteredForTable = accountUsersFiltered
    .filter(user => {
      if (uniqueDisplayNames.has(user.displayName)) {
        return false
      }
      uniqueDisplayNames.add(user.displayName)
      return true
    })
    .map(user => {
      return { text: user.displayName, value: user.displayName }
    })

  // Create filter options for the Table's Email address column based on the users in the account
  const accountUsersFilteredForTableEmail = accountUsersFiltered.map(user => {
    return { text: user.email, value: user.email }
  })

  const handleFormSubmit = () => {
    form.validateFields().then(values => {
      const user =
        stateUsers.users[getKeyFromIds(accountId, accountUser.userId)]
      const projectPermissions = []
      const projectSettings = []
      let forceGlobalNotificationsEnabled = false
      user.projectPermissions.forEach(permission => {
        const permissionUpdated = values.projectPermissions.find(
          p => p.projectId === permission.projectId
        )

        if (!permissionUpdated) {
          projectPermissions.push(permission)
        }
      })

      user.projectSettings.forEach(setting => {
        const permissionUpdated = values.projectPermissions.find(
          p => p.projectId === setting.projectId
        )
        if (!permissionUpdated) {
          projectSettings.push(setting)
        }
      })

      values.projectPermissions.forEach(permission => {
        if (permission.accessEnabled) {
          projectPermissions.push({
            userId: accountUser.userId,
            projectId: permission.projectId,
            userRoleId: getIdFromRole(permission.role)
          })
        }

        if (permission.notificationsEnabled) {
          forceGlobalNotificationsEnabled = true
        }

        projectSettings.push({
          userId: accountUser.userId,
          projectId: permission.projectId,
          notificationsEnabled: permission.notificationsEnabled
        })
      })

      setAlert(null)
      dispatch(
        userUpdateStart(
          accountId,
          accountUser.userId,
          projectPermissions,
          accountUser.notificationsEnabled || forceGlobalNotificationsEnabled,
          projectSettings
        )
      )
    })
  }

  const handleTableChange = (pagination, filters, sorter, extra) => {
    // `extra.currentDataSource` contains the filtered data
    setFilteredInfo(filters)
  }

  const handleResetFilters = () => {
    setFilteredInfo({})
  }

  const handleCancel = () => {
    setModalOpen(false)
    setAlert(null)
    dispatch(userEditStart({}))
  }

  // If all filters are null, empty string, or empty array, disable the reset button
  const allFiltersEmpty = Object.entries(filteredInfo).every(
    ([key, filter]) => {
      return (
        filter === null ||
        filter === '' ||
        (Array.isArray(filter) && filter.length === 0)
      )
    }
  )

  // Count the number of active filters to display in the badge
  const activeFilterCount = Object.entries(filteredInfo).reduce(
    (count, [key, filter]) => {
      if (
        (typeof filter === 'string' && filter !== '') ||
        (Array.isArray(filter) && filter.length > 0)
      ) {
        return count + 1
      }
      return count
    },
    0
  )

  const columns = [
    {
      width: '2rem',
      render: data => {
        return (
          <UserAvatar displayName={data.displayName} displayTooltip={false} />
        )
      }
    },
    {
      title: 'User',
      dataIndex: 'displayName',
      key: 'displayName',
      filters: accountUsersFilteredForTable,
      filteredValue: filteredInfo.displayName || null,
      filterSearch: true,
      onFilter: (value, record) => {
        if (record.displayName) {
          return record.displayName.includes(value)
        }
        return false
      }
    },
    {
      title: 'Email address',
      dataIndex: 'email',
      key: 'email',
      filters: accountUsersFilteredForTableEmail,
      filteredValue: filteredInfo.email || null,
      filterSearch: true,
      onFilter: (value, record) => {
        if (record.email) {
          return record.email.includes(value)
        }
        return false
      }
    },
    {
      width: '15rem',
      title: 'Last activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity'
    },
    {
      title: (
        <Tooltip title="Reset filters">
          <ResetFilters
            aria-label="Reset filters"
            disabled={allFiltersEmpty}
            onClick={handleResetFilters}
          >
            <Badge
              count={allFiltersEmpty ? 0 : activeFilterCount}
              offset={[-1, 1]}
              color={colours.CONSTRUCTION_ORANGE}
              styles={{
                root: {
                  color: 'inherit'
                }
              }}
            >
              <icons.ResetFilters size="22px" />
            </Badge>
          </ResetFilters>
        </Tooltip>
      ),
      width: '2rem',
      render: data => {
        return (
          <StyledClickableLink
            id="navigateToDetailsBtn"
            onClick={() => {
              dispatch(userEditStart(data.userData))
              form.setFieldsValue(getFormData(data.userData))
              setModalOpen(true)
            }}
            to={'#'}
            style={{ padding: '0.25rem' }}
          >
            <Tooltip title="Edit details">
              <icons.ViewRecord />
            </Tooltip>
          </StyledClickableLink>
        )
      }
    }
  ]

  const calculateUserData = () => {
    const headerRow = [
      'User name',
      'Email address',
      //'Account Admin', -- Currently Account Admin is not available
      'Reader Projects',
      'Contributor Projects',
      'Admin Projects',
      'Assignee projects',
      //'User created', -- Currently createdAt is not available
      'Last activity'
    ]

    const csvData = []
    const users = getTableData(stateUsers)?.filter(user =>
      matchesEmailFilter(auth.isQualisFlowInternal, '', user.email)
    )

    users.forEach(user => {
      csvData.push([
        user.displayName || '',
        user.email || '',
        // user.isAccountAdmin,
        user.userData?.projectPermissions
          ?.filter(permission => permission.userRoleId === 1)
          .map(
            permission =>
              stateAccountProjects.projects?.find(
                project => project.projectId === permission.projectId
              )?.name
          )
          .join(', ') || '',
        user.userData?.projectPermissions
          ?.filter(permission => permission.userRoleId === 2)
          .map(
            permission =>
              stateAccountProjects.projects?.find(
                project => project.projectId === permission.projectId
              )?.name
          )
          .join(', ') || '',
        user.userData?.projectPermissions
          ?.filter(permission => permission.userRoleId === 3)
          .map(
            permission =>
              stateAccountProjects.projects?.find(
                project => project.projectId === permission.projectId
              )?.name
          )
          .join(', ') || '',
        stateAccountProjects.projects
          ?.filter(
            project =>
              !user.userData?.projectPermissions.some(
                permission => project.projectId === permission.projectId
              )
          )
          .map(project => project.name)
          .join(', ') || '',
        // user.createdAt,
        user.lastActivity
      ])
    })

    return [headerRow, ...csvData]
  }

  const projectPhases = {
    1: 'Demolition',
    2: 'Excavation',
    3: 'Construction',
    4: 'Post-Construction',
    5: 'Modular',
    6: 'Post-Completion'
  }

  const calculateProjectData = () => {
    const headerRow = [
      'Project ID',
      'Project Name',
      'Delivery Email',
      'Waste Email',
      'Autodesk',
      'Procore',
      'One Click LCA',
      'EC3',
      'SmartWaste Default Project ID',
      'SW Project Phase',
      'SW Waste Enabled',
      'SW Delivery Enabled',
      'SW Email Addresses'
    ]

    const csvData = []
    accountSummary?.accountConnectionsSummary?.forEach(summary => {
      csvData.push([
        summary.projectId || '',
        summary.projectName || '',
        summary.provisioningEmailsDetails
          ? summary.provisioningEmailsDetails
              .filter(email => email.module === 'Delivery')
              .map(email => email.email)
              .join(', ')
          : '',
        summary.provisioningEmailsDetails
          ? summary.provisioningEmailsDetails
              .filter(email => email.module === 'Waste')
              .map(email => email.email)
              .join(', ')
          : '',
        summary.isAutodeskEnabledAndConfigured ? 'Yes' : 'No',
        summary.isProcoreEnabled ? 'Yes' : 'No',
        summary.isOneClickLcaEnabled ? 'Yes' : 'No',
        summary.isEC3Enabled ? 'Yes' : 'No',
        summary.smartWasteDetails?.defaultSWProjectId || '',
        summary.smartWasteDetails?.defaultProjectPhase
          ? projectPhases[summary.smartWasteDetails.defaultProjectPhase]
          : '',
        summary.smartWasteDetails?.isWasteEnabled ? 'Yes' : 'No',
        summary.smartWasteDetails?.isDeliveryEnabled ? 'Yes' : 'No',
        summary.smartWasteDetails?.emailConfigDetails
          ?.map(
            emailConfig =>
              `${emailConfig.email} (${emailConfig.swProjectId} ${projectPhases[emailConfig.projectPhase]})`
          )
          .join(', ') || ''
      ])
    })

    const sortedCsvData = csvData.sort((a, b) => {
      if (a[1] && b[1]) {
        return a[1].toUpperCase().localeCompare(b[1].toUpperCase())
      }
      return 0
    })

    return [headerRow, ...sortedCsvData]
  }

  return (
    <Space direction="vertical" size="middle">
      <Flex.Row
        style={{
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}
      >
        <div>
          <Title level={3}>
            {!stateAccount.name && 'Account'} {stateAccount.name} Settings
          </Title>
        </div>
      </Flex.Row>
      <FlexWrapperPanel>
        <FlexWrapper style={{ gap: '0.125rem' }}>
          <DataViewButtons
            id="usersButton"
            className={view === 'users' ? 'active' : ''}
            onClick={() => {
              changeView('users')
            }}
          >
            <icons.Person colour="currentColor" size={sizes.SMALL_MEDIUM} />
            <Spacer.Fine />
            Users
          </DataViewButtons>
          <DataViewButtons
            id="projectDataButton"
            className={view === 'projectData' ? 'active' : ''}
            onClick={() => {
              changeView('projectData')
            }}
            disabled={
              stateAccountProjects?.projects?.length === 0 ||
              stateAccountProjects.loading
            }
          >
            <icons.Data colour="currentColor" size={sizes.SMALL_MEDIUM} />
            <Spacer.Fine />
            Project Data
          </DataViewButtons>
          <DataViewButtons
            id="alertSettingsButton"
            className={view === 'alertSettings' ? 'active' : ''}
            onClick={() => {
              changeView('alertSettings')
            }}
          >
            <icons.ShieldTick colour="currentColor" size={sizes.SMALL_MEDIUM} />
            <Spacer.Fine />
            Alert Settings
          </DataViewButtons>
          <DataViewButtons
            id="filesButton"
            className={view === 'files' ? 'active' : ''}
            onClick={() => {
              changeView('files')
            }}
          >
            <icons.Document colour="currentColor" size={sizes.SMALL_MEDIUM} />
            <Spacer.Fine />
            Files
          </DataViewButtons>
        </FlexWrapper>
        <ButtonWrapper>
          {view === 'users' &&
            stateUsers?.users &&
            stateAccountProjects?.projects?.length > 0 && (
              <StyledCSVLink
                onClick={() => {
                  trackMixpanel('frontend_account_settings_user_data_export', {
                    account_id: accountId
                  })
                }}
                filename={`${stateAccount.name}-user-data.csv`}
                data={calculateUserData()}
              >
                <icons.Download size={sizes.SMALL} colour={'currentColor'} />
                <Spacer.Fine />
                Export User Data
              </StyledCSVLink>
            )}
          {view === 'projectData' && (
            <StyledCSVLink
              onClick={() => {
                trackMixpanel(
                  'frontend_account_settings_all_project_data_export',
                  {
                    account_id: accountId
                  }
                )
              }}
              filename={`${stateAccount.name}-project-data.csv`}
              data={calculateProjectData()}
            >
              <icons.Download size={sizes.SMALL} colour={'currentColor'} />
              <Spacer.Fine />
              Export Account Data
            </StyledCSVLink>
          )}
          <NavButtonDecorator to={'https://wkf.ms/3ZlSAuG'}>
            <ColourButton.Action>
              <icons.NewTabIcon colour="currentColor" />
              <Spacer.Fine />
              <Label isChild colour="inherit">
                Request New Project
              </Label>
            </ColourButton.Action>
          </NavButtonDecorator>
        </ButtonWrapper>
      </FlexWrapperPanel>

      {view === 'users' && (
        <>
          <Modal
            open={modalOpen}
            onOk={handleFormSubmit}
            confirmLoading={accountUser.loading}
            onCancel={handleCancel}
            width={700}
            title={
              <UserCard
                displayName={accountUser?.displayName}
                email={accountUser?.email}
              />
            }
            transitionName=""
            footer={[
              <ColourButton.Basic
                key="back"
                type="button"
                onClick={handleCancel}
                style={{ display: 'inline-block', marginRight: '.75rem' }}
              >
                Cancel
              </ColourButton.Basic>,
              <ColourButton.Action
                key="submit"
                type="button"
                onClick={handleFormSubmit}
                style={{ display: 'inline-block' }}
              >
                OK
              </ColourButton.Action>
            ]}
          >
            <Form
              form={form}
              layout="vertical"
              size="middle"
              requiredMark={false}
              onValuesChange={(changed, values) => {
                values.projectPermissions = values.projectPermissions.map(
                  (value, i) => {
                    if (changed.projectPermissions[i]) {
                      if (
                        changed.projectPermissions[i].accessEnabled === false
                      ) {
                        value.notificationsEnabled = false
                      }
                      if (
                        changed.projectPermissions[i].notificationsEnabled ===
                        true
                      ) {
                        value.accessEnabled = true
                      }
                    }
                    return value
                  }
                )

                form.setFieldsValue(values)
              }}
            >
              <div
                className="ant-table-content"
                data-testid="user-account-settings-table"
              >
                <ProjectsTable>
                  <thead className="ant-table-thead">
                    <tr>
                      <th id="project" className="ant-table-cell">
                        Project Name
                      </th>
                      <th id="accessEnabled" className="ant-table-cell">
                        Access Enabled
                      </th>
                      <th id="notificationsEnabled" className="ant-table-cell">
                        Notifications Enabled
                      </th>
                      <th id="access" className="ant-table-cell">
                        Role
                      </th>
                    </tr>
                  </thead>
                  <tbody className="ant-table-tbody">
                    <Form.List name="projectPermissions">
                      {fields => (
                        <>
                          {fields.map((field, index) => (
                            <tr
                              key={field.key}
                              className="ant-table-row ant-table-row-level-0"
                            >
                              <td
                                id="project"
                                key={field.key}
                                className="ant-table-cell"
                              >
                                {form.getFieldValue([
                                  'projectPermissions',
                                  field.name,
                                  'project'
                                ])}
                              </td>
                              <td id="accessEnabled" className="ant-table-cell">
                                <Form.Item
                                  name={[index, 'accessEnabled']}
                                  valuePropName="checked"
                                  style={{ marginBottom: '1px' }}
                                >
                                  <Switch
                                    loading={accountUser?.loading}
                                    size="small"
                                    style={{ verticalAlign: 'middle' }}
                                  />
                                </Form.Item>
                              </td>
                              <td
                                id="notificationsEnabled"
                                className="ant-table-cell"
                              >
                                <Form.Item
                                  name={[index, 'notificationsEnabled']}
                                  valuePropName="checked"
                                  style={{ marginBottom: '1px' }}
                                >
                                  <Switch
                                    loading={accountUser.loading}
                                    size="small"
                                    style={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center'
                                    }}
                                  />
                                </Form.Item>
                              </td>
                              <td
                                id="access"
                                className="ant-table-cell"
                                data-testid="access-level"
                              >
                                <Form.Item
                                  name={[index, 'role']}
                                  style={{ marginBottom: '1px' }}
                                >
                                  <Select
                                    style={{
                                      verticalAlign: 'middle',
                                      width: '100%'
                                    }}
                                    disabled={
                                      !form.getFieldValue([
                                        'projectPermissions',
                                        field.name,
                                        'accessEnabled'
                                      ]) || accountUser.loading
                                    }
                                    loading={accountUser.loading}
                                  >
                                    {Object.values(USER_ROLES).map(value => (
                                      <Select.Option value={value} key={value}>
                                        <Row>
                                          <Col span={8}>{value}</Col>
                                          <Col span={1} offset={13}>
                                            <Tooltip
                                              title={ROLE_INFORMATION[value]}
                                              placement="right"
                                            >
                                              <QuestionCircleFilled
                                                style={{
                                                  color: colours.TEAL
                                                }}
                                              />
                                            </Tooltip>
                                          </Col>
                                        </Row>
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                    </Form.List>
                  </tbody>
                </ProjectsTable>
              </div>
              {alert}
            </Form>
          </Modal>
          <ProjectsTableWrapper>
            <Table
              rowKey="userId"
              columns={columns}
              onChange={handleTableChange}
              dataSource={accountUsersFiltered}
              pagination={false}
              rowClassName={(record, index) => {
                if (index === 0) {
                  return 'table-row-filter'
                }
                return 'table-row-light'
              }}
              size="middle"
              loading={{
                spinning: stateUsers.loading,
                indicator: (
                  <div>
                    <SmallSpinner />
                  </div>
                )
              }}
            />
          </ProjectsTableWrapper>
        </>
      )}
      {view === 'projectData' && (
        <ProjectSelector
          dataView="projectData"
          selectedProjectId={selectedProjectId}
          setSelectedProjectId={setSelectedProjectId}
        />
      )}
      {view === 'alertSettings' && (
        <ProjectSelector
          dataView="alertSettings"
          selectedProjectId={selectedProjectId}
          setSelectedProjectId={setSelectedProjectId}
        />
      )}
      {view === 'files' && (
        <ProjectSelector
          dataView="files"
          selectedProjectId={selectedProjectId}
          setSelectedProjectId={setSelectedProjectId}
        />
      )}
    </Space>
  )
}

const FlexWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
`
const FlexWrapperPanel = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background-color: ${colours.WHITE};
  border-radius: 10px;
  padding: 0.75rem 1rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`
const DataViewButtons = styled(ColourButton.BasicBorderless)`
  font-weight: 500;

  &.active {
    background-color: ${colours.CORE_NAVY};
    color: ${colours.WHITE};
  }
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
`
const ProjectsTableWrapper = styled.div`
  background-color: ${colours.WHITE};
  border-radius: 10px;
  padding: 0.5rem 1.25rem 1.25rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`
const ProjectsTable = styled.table`
  table-layout: auto;
  th,
  td {
    width: 100%;
  }

  thead tr th,
  tbody tr td {
    height: 10px;
    padding-top: 1px;
    padding-bottom: 1px;
    padding-left: 15px;
  }

  #project {
    overflow-wrap: anywhere;
    width: 40%;
  }

  #accessEnabled {
    width: 15%;
  }

  #notificationsEnabled {
    width: 15%;
  }

  #access {
    width: 30%;
  }
`
const ResetFilters = styled.button`
  height: 32px;
  width: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background-color: transparent;

  &:disabled {
    cursor: not-allowed;
    color: ${colours.BATTLESHIP_GREY};
  }
`
const StyledClickableLink = styled(ClickableLink)`
  height: 32px;
  width: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    color: ${colours.TEAL};
  }
`
const StyledCSVLink = styled(CSVLink)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${colours.DARK_GREY};
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid ${colours.BORDER_GREY};
  border-radius: 5px;
  padding: 0.25rem 0.625rem;

  &:hover,
  &:focus,
  &:active {
    background-color: ${colours.CORE_NAVY};
    border-color: ${colours.CORE_NAVY};
    color: ${colours.WHITE};
  }
`
